import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiDownload, FiCheckCircle, FiCpu, FiShield, FiZap, FiDatabase, FiSettings } from 'react-icons/fi';
import HexagonBackground from './HexagonBackground';
import './productDetail.css';

import kiaImg from '../../assets/products/kia-img.png';
import nissanImmoImg from '../../assets/products/nissan-img.png';
import nissanSystemImg from '../../assets/products/nissan-system.png';
import delphiImg from '../../assets/products/delphi-img.png';
import dodgeImg from '../../assets/products/dodge-img.png';
import mistubishiImg from '../../assets/products/mistubishi.jpeg';

const productsData = {
  'nissan-immo-off': {
    title: 'Nissan-Immo-Off',
    description: 'Professional-grade IMMO-OFF solution for Nissan vehicles. This tool provides a comprehensive bypass for immobilizer systems, ensuring that vehicles can be started without an original key or in case of immobilizer failure. It features a simple one-click process that automatically identifies the ECU type, corrects checksums, and applies the patch in seconds. Trusted by hundreds of professional tuners worldwide, it\'s the ultimate tool for workshop diagnostics and ECU repairs.',
    version: 'v2.1.0',
    image: nissanImmoImg,
    features: [
      'Full IMMO-OFF solution',
      'Automatic checksum correction',
      'One-click patching process',
      'Supports CAN and K-Line systems',
      'No hardware modifications required'
    ],
    supportList: [
      {
        title: 'Hardware Identifiers',
        items: ['MCU Type: Renesas SH705x', 'Calibration ID: E311U4D_', 'Immo Type: NATS6 CAN']
      },
      {
        title: 'Common Vehicle Applications',
        items: ['Nissan Navara (2.5 dCi)', 'Nissan Almera II (2.2 dCi)', 'Nissan Cabstar (2.5 dCi)', 'Nissan X-Trail (T30/T31)', 'Nissan Primera (P12)']
      }
    ]
  },
  'kia-hyundai-system-off': {
    title: 'Kia-Hyundai-System-Off',
    description: 'Advanced system management tool for Kia and Hyundai ECUs. This software allows technicians to selectively disable engine systems like EGR, DPF, or specific sensor monitoring for performance testing and diagnostic purposes. It supports the latest communication protocols and provides a safe write environment to prevent ECU bricking. Perfect for high-performance tuning shops and specialized repair centers who require precision and reliability.',
    version: 'v1.5.4',
    image: kiaImg,
    features: [
      'Comprehensive system management',
      'Supports latest Kia/Hyundai protocols',
      'User-friendly graphical interface',
      'Safe write technology',
      'Regular database updates'
    ],
    supportList: [
      {
        title: 'High-End Models (4MB Flash - Aurix TC2xx)',
        items: [
          'CPEGD3.26.X (4MB)',
          'CPEGD3.20.1 (Aurix TC277TP)',
          'CPEGP3.20.2 (Aurix TC275TP)',
          'CPGPSH3.26.1 (Aurix TC275TP)',
          'CPGDSH3.26.2 (Aurix TC275TP)',
          'CPGPSH3.24.1 (Aurix TC275TP)'
        ]
      },
      {
        title: 'Mid-Range Models (2.5MB Flash - TC1782)',
        items: [
          'CPEGD2.20.X (2.5MB)',
          'CPEGD2.20.1 (TC1782)',
          'CPEGD2.20.2 (TC1782)',
          'CPEGD2.20.3 (TC1782)',
          'CPEGD2.20.4 (TC1782)',
          'CPGDSH2.26.1 (TC1782)',
          'CPGDSH2.2x.x (TC1782)'
        ]
      },
      {
        title: 'Compact Models (1.5MB Flash - TC1728)',
        items: [
          'CPEGD2.XX.X (1.5MB)',
          'CPEGP2.10.1 (TC1728)',
          'CPGPSH2.14.1 (TC1728)'
        ]
      }
    ]
  },
  'nissan-dtc-system-off': {
    title: 'Nissan-DTC-System-Off',
    description: 'Precision Diagnostic Trouble Code removal tool for Nissan. This software enables the permanent removal of specific error codes that trigger the "Check Engine" light, without affecting other diagnostic functions. It is particularly useful for modified vehicles or when certain components have been removed for racing or off-road use. The intuitive interface allows for batch processing of codes and provides detailed logs of all changes to ensure maximum safety.',
    version: 'v3.0.2',
    image: nissanSystemImg,
    features: [
      'Precise DTC removal',
      'Supports wide range of Nissan models',
      'No hardware modifications required',
      'Fast processing time',
      'Backup and Restore functionality'
    ],
    supportList: [
      {
        title: 'Supported ECU Type',
        items: ['Renault/Nissan UDS HITACHI 4MB']
      },
      {
        title: 'CAT-OFF / O2 Sensor Disable (DTCs)',
        items: [
          'P0420 - Catalyst System Efficiency Below Threshold',
          'P2097 - Post Catalyst Fuel Trim System Too Rich',
          'P0139 - O2 Sensor Circuit Slow Response',
          'P2270 - O2 Sensor Signal Stuck Lean',
          'P2271 - O2 Sensor Signal Stuck Rich',
          'P2A01 - O2 Sensor Circuit Range/Performance',
          'P0038 - HO2S Heater Control Circuit High',
          'P0037 - HO2S Heater Control Circuit Low',
          'P0141 - O2 Sensor Heater Circuit',
          'P0136 - O2 Sensor Circuit Malfunction',
          'P0138 - O2 Sensor Circuit High Voltage',
          'P0137 - O2 Sensor Circuit Low Voltage'
        ]
      },
      {
        title: 'Active Grill Shutter Disable (DTCs)',
        items: [
          'P059F - Active Grille Air Shutter Performance',
          'P159F - Active Grille Air Shutter Control',
          'U0284 - Lost Communication With Active Grill Shutter'
        ]
      }
    ]
  },
  'toyota-delphi-system-off': {
    title: 'Toyota-Delphi-System-Off',
    description: 'Professional Delphi ECU patching solution for Toyota petrol platforms. This software provides a stable IMMO OFF workflow with one-click binary processing and built-in compatibility for common Delphi hardware families. It also includes selected DTC OFF capabilities used in workshop diagnostics and controlled test environments. Designed for speed and reliability, the tool helps technicians process supported binaries with minimal manual intervention while preserving a clean, repeatable workflow.',
    version: 'v1.0.0',
    image: delphiImg,
    features: [
      'IMMO OFF processing for supported Delphi binaries',
      'Selected DTC OFF support for workshop diagnostics',
      'One-click patching workflow',
      'Supports major Delphi MCU families',
      'Fast binary processing for 1MB, 1.5MB, and 2MB files'
    ],
    supportList: [
      {
        title: 'Supported ECUs',
        items: ['Delphi petrol ECU', 'MCU: MPC565', 'MCU: SH72512', 'MCU: SH72544']
      },
      {
        title: 'Firmware Sizes',
        items: ['1024 KB', '1536 KB', '2048 KB']
      },
      {
        title: 'Core Functions',
        items: ['IMMO OFF', 'DTC OFF (selected codes)', 'Save Modified File workflow']
      }
    ]
  },
  'mitsubishi-immo-off': {
    title: 'Mitsubishi Immo Off',
    description: 'Professional Mitsubishi IMMO OFF solution for Melco MHXXX ECU platforms. Built for workshop use with a clean, repeatable file workflow and fast turnaround for supported binaries. The product focuses on reliable immobilizer bypass processing and a simple interface for technicians who need consistent results.',
    version: 'v2.0.0',
    image: mistubishiImg,
    features: [
      'Mitsubishi IMMO OFF workflow',
      'Designed for Melco MHXXX ECU families',
      'Fast online file processing',
      'Workshop-friendly repeatable results',
      'Protected release build'
    ],
    supportList: [
      {
        title: 'Product Focus',
        items: ['Mitsubishi Melco MHXXX series', 'IMMO OFF file service', 'Diagnostic workflow support']
      },
      {
        title: 'Suggested Use',
        items: ['Professional workshop use', 'ECU file processing', 'Emergency immobilizer bypass']
      }
    ]
  }
  ,
  'dodge-file-unlock': {
    title: 'Dodge File Unlock',
    description: 'Demo landing page for Dodge File Unlock project. This is a semester project showcase and links externally to the project site for full details and downloads.',
    version: 'v1.0.0 (demo)',
    image: dodgeImg,
    features: [
      'Project landing and branding',
      'Demo screenshots and description',
      'External link to hosted project'
    ],
    supportList: [
      {
        title: 'Project Links',
        items: ['Official Site: https://dodge.prinzetuner.com/']
      }
    ]
    ,
    externalSite: 'https://dodge.prinzetuner.com/'
  }
};

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const product = productsData[productId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!product) {
    return (
      <div className="gpt3__product-detail_error section__padding">
        <HexagonBackground />
        <h1 className="gradient__text">Product Not Found</h1>
        <button onClick={() => navigate('/')} className="gpt3__product-detail_back-btn">
          <FiArrowLeft /> Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="gpt3__product-detail section__padding">
      <HexagonBackground />

      <div className="gpt3__product-detail_container">
        <motion.button
          onClick={() => navigate('/')}
          className="gpt3__product-detail_back-btn"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <FiArrowLeft /> Back to Home
        </motion.button>

        {/* Hero Section - Split Layout */}
        <div className="gpt3__product-detail_hero-premium">
          <motion.div
            className="hero-image-side"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <img src={product.image} alt={`${product.title} Professional ECU Software Interface`} loading="lazy" />
          </motion.div>

          <motion.div
            className="hero-content-side"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="premium_badge">{product.version} Professional</span>
            <h1 className="gradient__text">{product.title}</h1>
            <p className="hero-desc-short">{product.description.substring(0, 200)}...</p>

            <div className="quick-specs-grid">
              <div className="spec-item">
                <FiZap /> <span>Instant Patch</span>
              </div>
              <div className="spec-item">
                <FiShield /> <span>Checksum Safe</span>
              </div>
              <div className="spec-item">
                <FiCpu /> <span>UDS/CAN Ready</span>
              </div>
            </div>

            <div className="hero-cta-group">
              {product.externalSite ? (
                <a href={product.externalSite} target="_blank" rel="noreferrer">
                  <button className="premium-download-btn">
                    <FiDownload /> Open Project Site
                  </button>
                </a>
              ) : (
                <a href="https://wa.me/966590707424" target="_blank" rel="noreferrer">
                  <button className="premium-download-btn">
                    <FiDownload /> Get Software Now
                  </button>
                </a>
              )}
            </div>
          </motion.div>
        </div>

        {/* Detailed Info Section */}
        <div className="gpt3__product-detail_content-grid">

          {/* Left Column: Technical Features */}
          <motion.div
            className="content-column-left"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="info-card">
              <h3><FiSettings /> Technical Features</h3>
              <ul className="premium-feature-list">
                {product.features.map((feature, index) => (
                  <li key={index}>
                    <FiCheckCircle className="check-icon" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="info-card description-card">
              <h3><FiDatabase /> Professional Overview</h3>
              <p>{product.description}</p>
            </div>
          </motion.div>

          {/* Right Column: Support List (The Star of the show) */}
          <motion.div
            className="content-column-right"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="support-list-premium-card">
              <div className="card-header">
                <FiCpu />
                <h3>Technical Compatibility</h3>
              </div>
              <div className="support-list-scroller">
                {product.supportList && product.supportList.map((section, idx) => (
                  <div key={idx} className="support-section-box">
                    <h4>{section.title}</h4>
                    <div className="support-items-flex">
                      {section.items.map((item, i) => (
                        <div key={i} className="support-tag">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
